import { useEffect, useState } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import OpportunityCard from "../components/OpportunityCard";
import LoadingSpinner from "../components/LoadingSpinner";
import EmptyState from "../components/EmptyState";

const Placements = () => {
  const { user } = useAuth();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [savedItems, setSavedItems] = useState({});
  const [filters, setFilters] = useState({ type: "all", location: "all" });
  const [sortOption, setSortOption] = useState("none");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  useEffect(() => {
    const fetchPlacements = async () => {
      try {
        const snap = await getDocs(collection(db, "placements"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPlacements(data);
      } catch (error) {
        console.error("Error fetching placements:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlacements();
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchSaved = async () => {
      const q = query(collection(db, "bookmarks"), where("userId", "==", user.uid));
      const snap = await getDocs(q);
      const saved = {};
      snap.forEach((d) => { saved[d.data().internshipId] = true; });
      setSavedItems(saved);
    };
    fetchSaved();
  }, [user]);

  const toggleBookmark = async (id) => {
    if (!user) {
      alert("Please sign in to save opportunities.");
      return;
    }
    const ref = doc(db, "bookmarks", `${user.uid}_${id}`);
    if (savedItems[id]) {
      await deleteDoc(ref);
      setSavedItems((prev) => ({ ...prev, [id]: false }));
    } else {
      await setDoc(ref, { userId: user.uid, internshipId: id });
      setSavedItems((prev) => ({ ...prev, [id]: true }));
    }
  };

  let filtered = placements.filter((job) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      job.title?.toLowerCase().includes(q) ||
      job.company?.toLowerCase().includes(q) ||
      job.location?.toLowerCase().includes(q);

    const matchesType =
      filters.type === "all" ||
      (filters.type === "fulltime" && job.type === "Full-time") ||
      (filters.type === "contract" && job.type === "Contract");

    const matchesLocation =
      filters.location === "all" ||
      (filters.location === "remote" && job.remote === true) ||
      (filters.location === "onsite" && job.remote === false);

    return matchesSearch && matchesType && matchesLocation;
  });

  let sorted = [...filtered];
  if (sortOption === "salary") sorted.sort((a, b) => (b.salary || 0) - (a.salary || 0));
  if (sortOption === "deadline") sorted.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <LoadingSpinner text="Loading placements..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="page-container">
        <div className="mb-8">
          <h1 className="section-title">Placement Opportunities</h1>
          <p className="text-gray-500 mt-2">Full-time job openings for freshers and experienced candidates.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="relative sm:col-span-2 lg:col-span-4">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by title, company, or location..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="input-field pl-11"
            />
          </div>

          <select
            value={filters.type}
            onChange={(e) => { setFilters({ ...filters, type: e.target.value }); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="all">Job Type</option>
            <option value="fulltime">Full-time</option>
            <option value="contract">Contract</option>
          </select>

          <select
            value={filters.location}
            onChange={(e) => { setFilters({ ...filters, location: e.target.value }); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="all">Work Mode</option>
            <option value="remote">Remote</option>
            <option value="onsite">On-Site</option>
          </select>

          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="filter-select"
          >
            <option value="none">Sort by</option>
            <option value="salary">Salary (High to Low)</option>
            <option value="deadline">Deadline (Soonest First)</option>
          </select>
        </div>

        {paginated.length === 0 ? (
          <EmptyState
            title="No placements found"
            message={placements.length === 0
              ? "Placement opportunities will appear here once they are added."
              : "Try adjusting your search or filters."}
          />
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in">
              {paginated.map((job) => (
                <OpportunityCard
                  key={job.id}
                  opportunity={job}
                  type="placement"
                  isSaved={!!savedItems[job.id]}
                  onToggleBookmark={toggleBookmark}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  className="button btn-secondary text-sm"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="button btn-secondary text-sm"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Placements;
